import React, { Component, ErrorInfo } from 'react';
import { crashState } from '../utils/crashHandler';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // Store errorInfo in state so it's available in other methods
    this.setState({ errorInfo });
  }

  handleFeedbackClick = () => {
    if (!this.state.error) return;

    crashState.next({ 
      log: {
        timestamp: new Date().toISOString(),
        error: this.state.error,
        componentStack: this.state.errorInfo?.componentStack || undefined,
        userAgent: navigator.userAgent,
        url: window.location.href,
        lastActions: []
      },
      showFeedback: true 
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-linear-to-r from-red-500 to-pink-600">
          <div className="text-center text-white p-8 max-w-md">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="mb-6">{this.state.error?.message}</p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.reload();
                }}
                className="w-full bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={this.handleFeedbackClick}
                className="w-full bg-transparent border-2 border-white text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                Report This Issue
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 